import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Ban, Building2, CreditCard, Mail, TriangleAlert, UserX } from 'lucide-react';
import { C, S, SORA, AUTH_BRAND_FONT, useLoginLayoutMode } from './loginAuthTheme';
import { foundation } from '../../ui/tokens';
import useReducedMotion from '../../hooks/ai/useReducedMotion';
import BrandLogo from '../common/BrandLogo';
import { useFocusRing } from '../../hooks/ui/useFocusRing';
import FillButtonContent from '../common/actionBeat/FillButtonContent';

const sans = foundation.fontFamily.sans;

/**
 * Self-contained "access restricted" screen for the three ways a signed-in
 * user can be locked out before the app renders: their own membership was
 * deactivated ('account'), their company was deactivated ('company'), or
 * tenant resolution failed for some other reason ('generic'). Visual spec:
 * docs/mockups/team-access-modals.html → "Access screens" (renderAccess()) —
 * 60px icon well, display-font title, one sentence, an optional signed-in-
 * email chip, ONE full-width primary "Cerrar sesión" button.
 *
 * Owns its OWN minimal frame (2026-09-24 revision) — previously rendered
 * inside `AuthScreenShell`, which flows the card from the top on phone,
 * appends a © line, and leaves a two-tone gradient background with the
 * lower half blank. This screen is a dead end (sign out is the only exit),
 * not a step in the login flow, so it gets no shell chrome: a single-canvas
 * full-viewport frame that centers ONE card, no copyright, no marketing
 * aside. The card renders its own CrewVex wordmark (`BrandLogo`, the shell
 * no longer does it for this screen) plus the icon well / title / body /
 * chip / button that were already here.
 *
 * Motion (founder standing order, "smooth morph always" + the modal exit
 * contract applied to a full-screen card): entrance is a one-time fade +
 * rise + settle on mount (translateY 22px -> 0, scale .98 -> 1, ~260ms
 * spring); sign-out plays an exit (fade + scale .96, ~200ms) before calling
 * `onSignOut`. Both are plain CSS transitions driven by a state flip on the
 * next frame / after a `setTimeout`, never `transitionend`/`animationend`
 * alone (PLAYBOOK anti-pattern: "driving a UI state machine off
 * animationend without a setTimeout backup"). Reduced motion skips every
 * transform and fires immediately.
 *
 * Icons reused (not invented): UserX is the app's existing "removed / no
 * access" glyph (src/components/admin/customers/CustomersBulkStatusAssignMenus.js).
 * Building2 is the app's canonical "Empresa" / company glyph (admin nav `nav.organization` →
 * src/components/layout/navigationSidebarNav.js:72, and every Platform
 * tenant surface). TriangleAlert (generic fallback) mirrors its use as the
 * app's standing "something needs attention" glyph (estimate/archive confirm
 * content, OfficeInboxModal, UnbilledInRangeTag).
 *
 * Button rule: a screen with a real next step passes `primaryAction` — it renders as the ONE blue button and
 * Sign out goes quiet (outlined). A screen with nothing to do keeps Sign out as the only (dark) button.
 */

// Local to this screen — NOT AuthScreenShell's AUTH_LEAVE_MS (220ms, a 6px
// sink): this is a full-screen card with its own entrance the shell never
// had, so it gets its own numbers rather than borrowing a value tuned for a
// shell-hosted step transition.
const ENTER_MS = 260;
const ENTER_EASE = 'cubic-bezier(0.34, 1.56, 0.64, 1)'; // BEAT_EASE.spring (actionBeatMotion.js)
const EXIT_MS = 200;
const EXIT_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const BUTTON_BASE = {
  width: '100%',
  minHeight: '48px',
  fontSize: '0.9375rem',
  fontWeight: 600,
  fontFamily: sans,
  borderRadius: '10px',
  boxSizing: 'border-box',
};

const VARIANT_ICON = { account: UserX, company: Building2, suspended: Ban, generic: TriangleAlert };

const VARIANT_COPY = {
  account: {
    titleId: 'auth.access.account.title',
    titleDefault: 'Your account is deactivated',
    bodyId: 'auth.access.account.body',
    bodyDefault: 'A company admin turned off your access. Ask them to reactivate it so you can sign back in.',
  },
  company: {
    titleId: 'auth.access.company.title',
    titleDefault: 'Company inactive',
    bodyId: 'auth.access.company.body',
    bodyDefault: "Your company's account is inactive, so no one can sign in right now. Contact your administrator.",
  },
  // KAN-204 Ship B — the company is SUSPENDED (a billing state, not a deactivation). Callers pass the
  // reason- and role-aware sentence as `bodyOverride` (companyAccessSuspendedScreen); this is the fallback.
  suspended: {
    titleId: 'companyAccess.screen.title',
    titleDefault: "Your company's account is suspended",
    bodyId: 'companyAccess.screen.body.subscribe',
    bodyDefault: 'Ask the owner to subscribe to start using CrewVex again.',
  },
};

/** The screen's action button: keyboard-only focus ring in the app's standard ring (never the browser's heavy default). */
function PrimaryActionButton({ action, common }) {
  const ring = useFocusRing();
  const reduced = useReducedMotion();
  // KAN-852: with a `busyLabel` the tap plays the FILL beat INSIDE this one box (same height, no dimming).
  const beat = Boolean(action.busyLabel);
  return (
    <button
      type="button"
      {...common}
      {...ring.focusProps}
      onClick={action.onClick}
      disabled={action.busy}
      aria-busy={action.busy || undefined}
      style={{
        ...common.style, outline: 'none', boxShadow: ring.focused ? `0 0 0 3px ${C.focusRing}` : 'none', transition: 'box-shadow 200ms ease',
        ...(beat ? { position: 'relative', overflow: 'hidden', opacity: 1 } : null),
      }}
    >
      <CreditCard size={16} strokeWidth={2.5} aria-hidden="true" style={{ flexShrink: 0, position: 'relative' }} />
      {beat ? (
        <FillButtonContent
          phase={action.busy ? 'working' : 'idle'}
          stage={action.busy ? 'run' : 'start'}
          reduced={reduced}
          confirmLabel={action.label}
          workingLabel={action.busyLabel}
          successLabel={action.busyLabel}
        />
      ) : action.label}
    </button>
  );
}

export default function AccessStatusScreen({ variant, email, message, bodyOverride, titleOverride, onSignOut, primaryAction, children }) {
  const intl = useIntl();
  const reduced = useReducedMotion();
  const brandFont = useLoginLayoutMode() === 'mobile' ? AUTH_BRAND_FONT.mobile : AUTH_BRAND_FONT.card;
  const [leaving, setLeaving] = useState(false);
  // Entrance flag — mounts `false` (pre-transform frame), flips `true` on the
  // next animation frame so the browser commits the start frame before the
  // transition begins. Reduced motion starts already `true` (no transform to
  // animate at all).
  const [entered, setEntered] = useState(reduced);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (reduced) return undefined;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const Icon = VARIANT_ICON[variant] ?? null;
  const copy = VARIANT_COPY[variant];
  const title = titleOverride != null ? titleOverride : copy
    ? intl.formatMessage({ id: copy.titleId, defaultMessage: copy.titleDefault })
    : intl.formatMessage({ id: 'auth.accessRestricted', defaultMessage: 'Access Restricted' });
  // bodyOverride lets a caller keep the variant's icon/title but swap the
  // sentence — e.g. PlatformRoute reuses 'account' (deactivated-user icon +
  // title) but a platform super-admin has no "empresa", so the tenant-flavored
  // default body ("A company admin turned off your access...") would be wrong.
  const body = bodyOverride != null
    ? bodyOverride
    : copy
      ? intl.formatMessage({ id: copy.bodyId, defaultMessage: copy.bodyDefault })
      : message;

  // Disable → play the exit beat (reduced motion: none, call immediately) →
  // call onSignOut. A plain setTimeout drives the call, never a
  // transitionend/animationend listener (PLAYBOOK anti-pattern: "driving a
  // UI state machine off animationend without a setTimeout backup").
  //
  // N6 (security re-review, 2026-09-24): if `onSignOut` throws or rejects,
  // reset `leaving` so the card becomes visible and clickable again — the
  // auth gate that mounted this screen still blocks its children regardless
  // of this local state, so a failed sign-out must never strand the person
  // on a permanently-disabled, invisible card with no way to retry.
  const handleSignOut = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    const finish = async () => {
      try {
        await onSignOut();
      } catch (_err) {
        setLeaving(false);
      }
    };
    if (reduced) {
      void finish();
      return;
    }
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      void finish();
    }, EXIT_MS);
  }, [leaving, reduced, onSignOut]);

  const showTransform = !reduced;
  const cardMotion = leaving
    ? {
        opacity: 0,
        transform: showTransform ? 'scale(0.96)' : undefined,
        transition: `opacity ${EXIT_MS}ms ${EXIT_EASE}, transform ${EXIT_MS}ms ${EXIT_EASE}`,
      }
    : {
        opacity: entered ? 1 : 0,
        transform: showTransform ? (entered ? 'translateY(0) scale(1)' : 'translateY(22px) scale(0.98)') : undefined,
        transition: showTransform ? `opacity ${ENTER_MS}ms ${ENTER_EASE}, transform ${ENTER_MS}ms ${ENTER_EASE}` : 'none',
      };

  return (
    <div
      data-testid="access-status-frame"
      style={{
        minHeight: '100dvh',
        width: '100%',
        background: C.page,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 'max(16px, env(safe-area-inset-top, 0px))',
        paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
        paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
        boxSizing: 'border-box',
        fontFamily: sans,
      }}
    >
      <div
        data-testid="access-status-screen"
        data-variant={variant}
        style={{
          width: '100%',
          maxWidth: '420px',
          maxHeight: 'calc(100dvh - 32px)',
          overflowY: 'auto',
          boxSizing: 'border-box',
          borderRadius: '22px',
          border: `1px solid ${C.border}`,
          boxShadow: '0 4px 32px rgba(15,23,42,0.08)',
          backgroundColor: C.white,
          padding: 'clamp(22px, 5vw, 32px) clamp(18px, 5vw, 24px)',
          textAlign: 'center',
          pointerEvents: leaving ? 'none' : undefined,
          ...cardMotion,
        }}
      >
        <div data-testid="auth-brand" style={{ textAlign: 'center', marginBottom: '14px' }}>
          {/* Same wordmark size as every sign-in card (AUTH_BRAND_FONT). */}
          <BrandLogo fontSize={brandFont} crewColor={C.heading} />
        </div>
        <div
          aria-hidden="true"
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            margin: '0 auto 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: C.bg,
            color: C.readable,
          }}
        >
          {Icon ? <Icon data-testid="access-icon" size={26} strokeWidth={2} /> : null}
        </div>
        <div style={{ fontFamily: SORA, fontSize: '1.375rem', fontWeight: 700, color: C.heading, margin: '0 0 8px' }}>
          {title}
        </div>
        <div
          style={{
            fontFamily: sans,
            fontSize: '0.9375rem',
            fontWeight: 500,
            color: C.body,
            lineHeight: 1.45,
            margin: '0 auto',
            maxWidth: '330px',
          }}
        >
          {body}
        </div>
        {primaryAction ? (() => {
          const common = {
            'data-testid': primaryAction.testId,
            style: {
              ...BUTTON_BASE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginTop: '18px',
              backgroundColor: foundation.color.brand.primary,
              border: `1px solid ${foundation.color.brand.primary}`,
              color: C.btnText,
              textDecoration: 'none',
              cursor: primaryAction.busy ? 'default' : 'pointer',
              opacity: primaryAction.busy && !primaryAction.busyLabel ? 0.7 : 1,
            },
          };
          // KAN-196: an action (Stripe checkout / portal) or, as before, an email link.
          if (primaryAction.onClick) return <PrimaryActionButton action={primaryAction} common={common} />;
          return (
            <>
              <a href={primaryAction.href} {...common}>
                <Mail size={16} strokeWidth={2.5} aria-hidden="true" style={{ flexShrink: 0 }} />
                {primaryAction.label}
              </a>
              {primaryAction.address ? (
                <div data-testid="access-support-address" style={{ marginTop: foundation.spacePx[2], fontSize: foundation.fontSize.sm, fontWeight: 500, color: C.readable, userSelect: 'text' }}>
                  {primaryAction.address}
                </div>
              ) : null}
            </>
          );
        })() : null}
        {children ? (
          <div data-testid="access-status-extra" style={{ marginTop: '18px', textAlign: 'left' }}>
            {children}
          </div>
        ) : null}
        {email ? (
          <div
            data-testid="access-email-chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: `${S.md}px`,
              fontFamily: sans,
              fontSize: '0.78rem',
              fontWeight: 600,
              color: C.body,
              backgroundColor: C.bg,
              borderRadius: '999px',
              padding: '6px 11px',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Mail size={13} strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {email}
            </span>
          </div>
        ) : null}
        <button
          type="button"
          onClick={handleSignOut}
          disabled={leaving}
          style={{
            ...BUTTON_BASE,
            marginTop: primaryAction ? '12px' : '18px',
            ...(primaryAction
              ? { backgroundColor: C.white, color: C.heading, border: `1px solid ${C.border}` }
              : { backgroundColor: C.btnBg, color: C.btnText, border: 'none' }),
            cursor: leaving ? 'default' : 'pointer',
            opacity: leaving ? 0.7 : 1,
          }}
        >
          {intl.formatMessage({ id: 'auth.signOut', defaultMessage: 'Sign Out' })}
        </button>
      </div>
    </div>
  );
}

AccessStatusScreen.propTypes = {
  variant: PropTypes.oneOf(['account', 'company', 'suspended', 'generic']).isRequired,
  email: PropTypes.string,
  message: PropTypes.node,
  bodyOverride: PropTypes.node,
  /** Optional (KAN-196): swaps the variant's title; every other caller keeps the default. */
  titleOverride: PropTypes.node,
  onSignOut: PropTypes.func.isRequired,
  /** The screen's one blue button: { label, href | onClick, busy?, testId? } (an email link or an action). Sign out turns outlined. */
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired, href: PropTypes.string, address: PropTypes.string, onClick: PropTypes.func, busy: PropTypes.bool, testId: PropTypes.string,
  }),
  /** Legacy: extra content under the sentence; unused now (the suspended plan card was removed). */
  children: PropTypes.node,
};
